import Check from '@mui/icons-material/Check';
import { Box, Typography, useTheme } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { borderWidth } from '~/styles/tokens/shape';

const CHECKOUT_LABELS = ['Cotação', 'Pagamento', 'Dados do contrato'] as const;

type StepVisual = 'done' | 'current' | 'upcoming';

type CheckoutFlowStepperProps = {
  /** `3` = fluxo concluído: as três etapas aparecem com check. */
  activeIndex?: 0 | 1 | 2 | 3;
};

function stepVisual(index: number, activeIndex: number): StepVisual {
  if (index < activeIndex) return 'done';
  if (index === activeIndex) return 'current';
  return 'upcoming';
}

function circleStyles(theme: Theme, visual: StepVisual) {
  const borderMuted = theme.palette.divider;
  const baseSize = theme.spacing(2);
  const baseStyles = {
    alignItems: 'center',
    borderRadius: '100px',
    display: 'flex',
    flexShrink: 0,
    fontSize: theme.typography.subtitle2.fontSize,
    height: baseSize,
    justifyContent: 'center',
    width: baseSize
  } as const;
  if (visual === 'done') {
    return {
      ...baseStyles,
      bgcolor: theme.palette.primary.main,
      border: `${borderWidth.small}px solid ${theme.palette.primary.main}`,
      color: theme.palette.primary.contrastText
    };
  }
  if (visual === 'current') {
    return {
      ...baseStyles,
      border: `${borderWidth.small}px solid ${theme.palette.primary.main}`,
      color: theme.palette.text.primary
    };
  }
  return {
    ...baseStyles,
    border: `${borderWidth.small}px solid ${borderMuted}`,
    color: theme.palette.text.secondary
  };
}

export default function CheckoutFlowStepper({ activeIndex = 0 }: CheckoutFlowStepperProps) {
  const theme = useTheme();
  const trackFillPercent = Math.min(100, Math.round(((activeIndex + 1) / CHECKOUT_LABELS.length) * 100));

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        maxWidth: 1234,
        mx: 'auto',
        pb: 1,
        pt: 2,
        px: { sm: 2, xs: 1 },
        width: '100%'
      }}
    >
      <Box
        sx={{
          bgcolor: theme.palette.background.paper,
          borderRadius: `${theme.spacing(2)}`,
          columnGap: { md: '80px', sm: theme.spacing(2), xs: theme.spacing(0.5) },
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'nowrap',
          justifyContent: { sm: 'center', xs: 'space-between' },
          px: { sm: 2, xs: 1 },
          py: theme.spacing(1.5),
          rowGap: 1,
          width: '100%'
        }}
      >
        {CHECKOUT_LABELS.map((label, index) => {
          const visual = stepVisual(index, activeIndex);
          const emphasis = visual !== 'upcoming';
          return (
            <Box
              alignItems="center"
              component="section"
              data-app-id={`checkout-step-${index + 1}`}
              display="flex"
              gap={{ sm: theme.spacing(1), xs: theme.spacing(0.5) }}
              justifyContent="center"
              key={label}
              minWidth={0}
              sx={{
                opacity: emphasis ? 1 : 0.55
              }}
            >
              <Box aria-current={visual === 'current' ? 'step' : undefined} sx={() => circleStyles(theme, visual)}>
                {visual === 'done' ? (
                  <Check aria-hidden sx={{ fontSize: 16 }} />
                ) : (
                  <Typography component="span" variant="subtitle2">
                    {index + 1}
                  </Typography>
                )}
              </Box>
              <Typography noWrap sx={{ fontWeight: visual === 'current' ? 600 : 500 }} variant="subtitle2">
                {label}
              </Typography>
            </Box>
          );
        })}
      </Box>
      <Box sx={{ mt: 1.5, px: 0.5, width: '100%' }}>
        <Box
          sx={{
            bgcolor: theme.palette.secondary.main,
            borderRadius: 99,
            height: 6,
            overflow: 'hidden',
            width: '100%'
          }}
        >
          <Box
            sx={{
              background: 'linear-gradient(180deg, #e2c7ff 0%, #9537fc 48%, #33006a 100%)',
              borderRadius: 99,
              height: '100%',
              maxWidth: '100%',
              transition: 'width 0.35s ease-out',
              width: `${trackFillPercent}%`
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}
